<script>

///////////////////////////////////////////////////////////////////////////////////////////////////////
//
//  Icon
//
///////////////////////////////////////////////////////////////////////////////////////////////////////

import { defineComponent, ref, computed } from 'vue'


/**
 * Ex1
 * template code with the from-project
 * 
 * <template>
 *   
 *   <Icon width="2rem" from-project="true" height="2rem" style="fill:#0066CC;" icon="my-cool-arrow"/>  
 * 
 * </template>
 * 
 * Ex2 
 * template code, with file coming from the base project
 * 
 * <template>
 *   
 *   <Icon width="2rem" height="2rem" style="fill:#0066CC;" icon="rounded-single-chevron-right"/>  
 * 
 * </template>
 * 
 * Ex2
 * using a css class for the style and fill
 * 
 * <style scoped>
 *
 * .icon {
 *   transform:translateY(50%);
 *   fill:var(--link);
 * }
 *
 * </style>
 * 
 * <template>
 * 
 *   <Icon width="2rem" height="2rem" class="icon" icon="rounded-single-chevron-right"/>  
 * 
 * </template>
 * 
 * 
 * Ex3
 * The icon file with polygon
 * 
 * export const viewBox = "0 0 70.711 91.897"
 * export const shapes = {type:"polygon", data:"29.882 45.949 1.661 1.676 69.793 45.949 1.661 90.221 29.882 45.949"}
 *
 * 
 * Ex4
 * The icon file with shape
 * 
 * export const viewBox="0 0 448 512"
 * export const shapes = {type:"path", data:"M224 0c70.7 0 128 57.3 128 128s-57.3 128-128 128s-128-57.3-128-128S153.3 0 224 0zM209.1 359.2l-18.6-31c-6.4-10.7 1.3-24.2 13.7-24.2H224h19.7c12.4 0 20.1 13.6 13.7 24.2l-18.6 31 33.4 123.9 39.5-161.2c77.2 12 136.3 78.8 136.3 159.4c0 17-13.8 30.7-30.7 30.7H265.1 182.9 30.7C13.8 512 0 498.2 0 481.3c0-80.6 59.1-147.4 136.3-159.4l39.5 161.2 33.4-123.9z" }
 * 
 */

export default defineComponent({
  name: "Icon",
  props: {
    icon: {
      type: String,
      required: false
    },
    fromProject: {
      type: Boolean,
      required: false, 
      default: false
    }
  },
  setup(props) {

    //The icon path and viewbox
    const iconPath = ref("")
    const iconViewBox = ref("")

    if(props.fromProject) {
      import(`@project/icons/${props.icon}.js`).then(val => {
        //the icon is just stored as a module with a path and viewBox string
        const { shapes, viewBox} = val
        iconPath.value = shapes
        iconViewBox.value = viewBox
      })
    } else {
      import(`@kit/assets/icons/${props.icon}.js`).then(val => {
        //the icon is just stored as a module with a path and viewBox string
        const { shapes, viewBox} = val
        iconPath.value = shapes
        iconViewBox.value = viewBox
      })
    }
  

    /**
     * Is the icon's path an array?
     * 
     */
    const iconIsMultiPath = computed(() => {
      const shapes = iconPath.value 
      if(shapes) {
        if(shapes.constructor == Array) {
          return true
        } else 
        if(shapes.constructor == String || shapes.constructor == Object) {
          return false
        }
      }
      return false
    })

    return { 
      iconPath,
      iconViewBox,
      iconIsMultiPath,
    }
  }
});
</script>

<template>

    <svg 
      v-if="iconPath"
      xmlns="http://www.w3.org/2000/svg" 
      :viewBox="iconViewBox"
      fill="currentColor">
      <template v-if="iconIsMultiPath">
        <template v-for="shape in iconPath">
          <template v-if="shape.type == 'path'">
            <path :d="shape.data"/>
          </template>
          <template v-else-if="shape.type == 'polygon'">
            <polygon :points="shape.data"/>
          </template>
        </template>
      </template>
      <template v-else>
        <path v-if="iconPath.type == 'path'" :d="iconPath.data"/>
        <polygon v-else-if="iconPath.type == 'polygon'" :points="iconPath.data" />
      </template>
    </svg>

</template>



